import React, { useState } from 'react';
import {
  Box,
  Button,
  Checkbox,
  FormControlLabel,
  TextField,
} from '@mui/material';
import { FormattedMessage } from 'react-intl';
import { DigitalQuestion } from '../../redux/types';

export type Props<T extends DigitalQuestion> = {
  surveyId: number;
  sectionId?: number;
  question?: T;
  addQuestion?: (dq: Omit<T, 'id'>) => void
  updateQuestion?: (dq: T) => void
  onCancelEdit?: () => void;
  type: T['type'];
  disableHideAggregation?: boolean;
}

const TextQuestionEditor = <T extends DigitalQuestion>({
  surveyId,
  sectionId,
  question,
  addQuestion,
  updateQuestion,
  onCancelEdit,
  type: qtype,
  disableHideAggregation,
}: Props<T>): JSX.Element => {
  const [text, setText] = useState<string>(question?.text || '');
  const [placeholder, setPlaceholder] = useState<string>(question?.placeholder || '');
  const [
    hideFromAggregation, setHideFromAggregation,
  ] = useState<boolean>(question?.hideAggregation || false);

  const onQuestionChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setText(e.target.value);
  };

  const onPlaceholderChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setPlaceholder(e.target.value);
  };

  const onAddClick = () => {
    if (!text.trim()) {
      return;
    }
    const newQuestion: Omit<T, 'id'> = {
      digitalSurveyId: surveyId,
      digitalSectionId: sectionId,
      text,
      placeholder: placeholder.trim(),
      hideAggregation: hideFromAggregation,
      type: qtype,
    } as Omit<T, 'id'>;
    if (addQuestion) {
      addQuestion(newQuestion);
      setText('');
      setPlaceholder('');
      setHideFromAggregation(false);
    }
  };

  const onUpdateClick = () => {
    if (!question) {
      return;
    }
    const updatedQuestion: T = {
      ...question,
      text,
      placeholder,
      hideAggregation: hideFromAggregation,
    };
    if (updateQuestion) {
      updateQuestion(updatedQuestion);
    }
  };

  const onCancelClick = () => {
    if (onCancelEdit) {
      onCancelEdit();
    }
  };

  const onHideFromAggregation = (e: React.SyntheticEvent<Element, Event>, checked: boolean) => {
    setHideFromAggregation(checked);
  };

  return (
    <Box>
      <Box>
        <TextField
          label="Enter your question here"
          value={text}
          onChange={onQuestionChange}
          fullWidth
          sx={{ mb: 1 }}
          variant="standard"
          size="small"
        />
        <TextField
          label="Enter placeholder text here"
          value={placeholder}
          onChange={onPlaceholderChange}
          variant="standard"
          fullWidth
        />
        {
          !disableHideAggregation && (
            <FormControlLabel
              label={<FormattedMessage id="questionEditor.hideAggregation" />}
              control={<Checkbox />}
              onChange={onHideFromAggregation}
              checked={hideFromAggregation}
            />
          )
        }
      </Box>
      <Box display="flex" gap={1} justifyContent="flex-end" alignContent="space-between" sx={{ my: 1 }}>
        {
          !question && (
            <Button size="small" variant="outlined" onClick={onAddClick}>Add</Button>
          )
        }
        {
          question && (
            <>
              <Button size="small" variant="outlined" onClick={onUpdateClick}>Update</Button>
              <Button size="small" variant="outlined" onClick={onCancelClick} color="secondary">Cancel</Button>
            </>
          )
        }
      </Box>
    </Box>
  );
};

export default TextQuestionEditor;
